/**
 * Tenant-Aware Locale Formatting — Pure Functions
 *
 * Centralized formatting for currency, dates, and numbers using tenant regional settings.
 * Reads defaults from localStorage (same pattern as timezone.ts).
 * For React components, prefer the useLocaleFormat() hook which reads from Zustand.
 *
 * SAP Alignment:
 * - Currency code and locale are separate concerns (SAP TCURX / T001-WAERS)
 * - Locale drives number formatting (grouping, decimals), currency code drives symbol
 * - Date format comes from tenant settings (SAP USR01-DATFM)
 */

import { formatInTimeZone } from 'date-fns-tz';
import { getTenantTimezone } from './timezone';

// ---------------------------------------------------------------------------
// LocalStorage keys (persisted during login)
// ---------------------------------------------------------------------------

const LOCALE_KEY = 'tenant_locale';
const CURRENCY_KEY = 'tenant_currency';
const DATE_FORMAT_KEY = 'tenant_date_format';
const TIME_FORMAT_KEY = 'tenant_time_format';

// ---------------------------------------------------------------------------
// Defaults (India — matches Prisma Tenant model defaults)
// ---------------------------------------------------------------------------

const DEFAULT_LOCALE = 'en-IN';
const DEFAULT_CURRENCY = 'INR';
const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
const DEFAULT_TIME_FORMAT = '12h';

// ---------------------------------------------------------------------------
// Getters / Setters (called during login/logout)
// ---------------------------------------------------------------------------

export function getTenantLocale(): string {
  return localStorage.getItem(LOCALE_KEY) ?? DEFAULT_LOCALE;
}

export function getTenantCurrency(): string {
  return localStorage.getItem(CURRENCY_KEY) ?? DEFAULT_CURRENCY;
}

export function getTenantDateFormat(): string {
  return localStorage.getItem(DATE_FORMAT_KEY) ?? DEFAULT_DATE_FORMAT;
}

export function getTenantTimeFormat(): string {
  return localStorage.getItem(TIME_FORMAT_KEY) ?? DEFAULT_TIME_FORMAT;
}

export function setTenantLocaleSettings(settings: {
  locale?: string;
  currency?: string;
  dateFormat?: string;
  timeFormat?: string;
}): void {
  if (settings.locale) localStorage.setItem(LOCALE_KEY, settings.locale);
  if (settings.currency) localStorage.setItem(CURRENCY_KEY, settings.currency);
  if (settings.dateFormat) localStorage.setItem(DATE_FORMAT_KEY, settings.dateFormat);
  if (settings.timeFormat) localStorage.setItem(TIME_FORMAT_KEY, settings.timeFormat);
}

export function clearTenantLocaleSettings(): void {
  localStorage.removeItem(LOCALE_KEY);
  localStorage.removeItem(CURRENCY_KEY);
  localStorage.removeItem(DATE_FORMAT_KEY);
  localStorage.removeItem(TIME_FORMAT_KEY);
}

// ---------------------------------------------------------------------------
// Date format mapping: tenant setting → date-fns-tz format string
// ---------------------------------------------------------------------------

const DATE_FORMAT_MAP: Record<string, string> = {
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'DD-MM-YYYY': 'dd-MM-yyyy',
};

function getDateFnsFormat(tenantFormat?: string): string {
  const fmt = tenantFormat ?? getTenantDateFormat();
  return DATE_FORMAT_MAP[fmt] ?? 'dd/MM/yyyy';
}

// ---------------------------------------------------------------------------
// Currency Formatting
// ---------------------------------------------------------------------------

/**
 * Format a number as currency using tenant's locale and currency settings.
 * Returns '—' for null/undefined/non-finite values.
 *
 * @example formatTenantCurrency(1500) → "₹1,500.00" (for en-IN / INR)
 * @example formatTenantCurrency(1500, 'USD', 'en-US') → "$1,500.00"
 */
export function formatTenantCurrency(
  amount: number | null | undefined,
  currency?: string,
  locale?: string,
): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  const cur = currency ?? getTenantCurrency();
  const loc = locale ?? getTenantLocale();
  try {
    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: cur,
    }).format(amount);
  } catch {
    return `${cur} ${amount.toFixed(2)}`;
  }
}

/**
 * Get just the currency symbol for the tenant's currency.
 * @example getTenantCurrencySymbol() → "₹"
 */
export function getTenantCurrencySymbol(currency?: string, locale?: string): string {
  const cur = currency ?? getTenantCurrency();
  const loc = locale ?? getTenantLocale();
  try {
    const parts = new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: cur,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? cur;
  } catch {
    return cur;
  }
}

// ---------------------------------------------------------------------------
// Number Formatting
// ---------------------------------------------------------------------------

/**
 * Format a number using tenant's locale (grouping separators, decimal style).
 * @example formatTenantNumber(15000) → "15,000" (en-IN) or "15,000" (en-US)
 */
export function formatTenantNumber(
  num: number | null | undefined,
  locale?: string,
  options?: Intl.NumberFormatOptions,
): string {
  if (num == null || !Number.isFinite(num)) return '—';
  const loc = locale ?? getTenantLocale();
  return new Intl.NumberFormat(loc, options).format(num);
}

// ---------------------------------------------------------------------------
// Date Formatting
// ---------------------------------------------------------------------------

/**
 * Format a date using tenant's date format and timezone.
 * Use for: display dates in tables, badges, detail pages.
 *
 * @example formatTenantDate('2026-04-09T10:30:00Z') → "09/04/2026" (DD/MM/YYYY, Asia/Kolkata)
 */
export function formatTenantDate(
  date: string | Date | null | undefined,
  dateFormat?: string,
  timezone?: string,
): string {
  if (!date) return '—';
  const tz = timezone ?? getTenantTimezone();
  const fmt = getDateFnsFormat(dateFormat);
  try {
    return formatInTimeZone(new Date(date), tz, fmt);
  } catch {
    return String(date);
  }
}

/**
 * Format a date+time using tenant's date format, time format, and timezone.
 * Use for: timestamps, approval times, audit logs.
 *
 * @example formatTenantDateTime('2026-04-09T10:30:00Z') → "09/04/2026 04:00 PM" (DD/MM/YYYY 12h, IST)
 */
export function formatTenantDateTime(
  date: string | Date | null | undefined,
  dateFormat?: string,
  timeFormat?: string,
  timezone?: string,
): string {
  if (!date) return '—';
  const tz = timezone ?? getTenantTimezone();
  const dateFmt = getDateFnsFormat(dateFormat);
  const tf = timeFormat ?? getTenantTimeFormat();
  const timeFmt = tf === '24h' ? 'HH:mm' : 'hh:mm a';
  try {
    return formatInTimeZone(new Date(date), tz, `${dateFmt} ${timeFmt}`);
  } catch {
    return String(date);
  }
}

/**
 * Format time-only using tenant's time format and timezone.
 * @example formatTenantTime('2026-04-09T10:30:00Z') → "04:00 PM" (12h, IST)
 */
export function formatTenantTime(
  date: string | Date | null | undefined,
  timeFormat?: string,
  timezone?: string,
): string {
  if (!date) return '—';
  const tz = timezone ?? getTenantTimezone();
  const tf = timeFormat ?? getTenantTimeFormat();
  const fmt = tf === '24h' ? 'HH:mm' : 'hh:mm a';
  try {
    return formatInTimeZone(new Date(date), tz, fmt);
  } catch {
    return String(date);
  }
}
